"use client";

import {
  BadgeCheck,
  Clapperboard,
  Globe,
  Layers,
  Palette,
  Sparkles,
  Tag,
  Type,
  Upload,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import { ProjectCard } from "@/components/project-card";
import { ProjectMediaCarousel } from "@/components/project-media-carousel";
import { ApiError, ApiValidationIssue, submitProject } from "@/lib/api";
import { uploadMediaFiles } from "@/lib/media";
import { ProjectSubmission } from "@/types/project";

const STEPS = ["stepBasics", "stepPlatforms", "stepMedia", "stepReview"] as const;
const STEP_ICONS = [Type, Layers, Upload, BadgeCheck] as const;
type BasicField = "title" | "description" | "tagline" | "external_url";
type FieldErrors = Partial<Record<BasicField, string>>;

function parseCsv(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function validateBasics(
  values: ProjectSubmission,
  t: (key: string, values?: Record<string, string | number>) => string
): FieldErrors {
  const errors: FieldErrors = {};

  if (!values.title.trim()) {
    errors.title = t("requiredTitle");
  } else if (values.title.trim().length < 3) {
    errors.title = t("minTitle");
  }

  if (!values.tagline.trim()) {
    errors.tagline = t("requiredTagline");
  } else if (values.tagline.trim().length < 8) {
    errors.tagline = t("minTagline");
  } else if (values.tagline.trim().length > 100) {
    errors.tagline = t("maxTagline");
  }

  if (!values.description.trim()) {
    errors.description = t("requiredDescription");
  } else if (values.description.trim().length < 20) {
    errors.description = t("minDescription");
  }

  if (!values.external_url.trim()) {
    errors.external_url = t("requiredExternalUrl");
  } else {
    try {
      const parsed = new URL(values.external_url);
      if (!parsed.protocol.startsWith("http")) {
        errors.external_url = t("invalidExternalUrlProtocol");
      }
    } catch {
      errors.external_url = t("invalidExternalUrl");
    }
  }

  return errors;
}

function getBasicFieldError(
  values: ProjectSubmission,
  field: BasicField,
  t: (key: string, values?: Record<string, string | number>) => string
): string | undefined {
  return validateBasics(values, t)[field];
}

function mapBackendValidation(details: ApiValidationIssue[] | string | undefined): FieldErrors {
  if (!Array.isArray(details)) {
    return {};
  }

  const errors: FieldErrors = {};
  for (const issue of details) {
    const field = issue.loc?.[1];
    if (field === "title" || field === "description" || field === "tagline" || field === "external_url") {
      errors[field] = issue.msg;
    }
  }

  return errors;
}

export function SubmissionWizard() {
  const t = useTranslations("Submission");
  const [step, setStep] = useState(0);
  const [state, setState] = useState<ProjectSubmission>({
    title: "",
    description: "",
    tagline: "",
    external_url: "",
    platforms: [],
    action_tags: [],
    image_urls: [],
    video_urls: [],
  });
  const [platformInput, setPlatformInput] = useState("web");
  const [actionTagsInput, setActionTagsInput] = useState("Generate Mock DB Entries, Compress Image locally");
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);
  const [uploadedVideoUrls, setUploadedVideoUrls] = useState<string[]>([]);
  const [localPreviewImageUrls, setLocalPreviewImageUrls] = useState<string[]>([]);
  const [localPreviewVideoUrls, setLocalPreviewVideoUrls] = useState<string[]>([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const previewImageUrls = uploadedImageUrls.length ? uploadedImageUrls : localPreviewImageUrls;
  const previewVideoUrls = uploadedVideoUrls.length ? uploadedVideoUrls : localPreviewVideoUrls;

  useEffect(() => {
    return () => {
      for (const url of [...localPreviewImageUrls, ...localPreviewVideoUrls]) {
        URL.revokeObjectURL(url);
      }
    };
  }, [localPreviewImageUrls, localPreviewVideoUrls]);

  const canContinue = useMemo(() => {
    if (step === 0) {
      return Object.keys(validateBasics(state, t)).length === 0;
    }
    return true;
  }, [step, state, t]);

  function updateField<K extends BasicField>(field: K, value: string) {
    setState((prev) => {
      const next = { ...prev, [field]: value };
      const nextFieldError = getBasicFieldError(next, field, t);
      setFieldErrors((current) => {
        if (!nextFieldError) {
          const nextErrors = { ...current };
          delete nextErrors[field];
          return nextErrors;
        }
        return { ...current, [field]: nextFieldError };
      });
      return next;
    });
  }

  function validateFieldOnBlur(field: BasicField) {
    const nextFieldError = getBasicFieldError(state, field, t);
    setFieldErrors((current) => {
      if (!nextFieldError) {
        const nextErrors = { ...current };
        delete nextErrors[field];
        return nextErrors;
      }
      return { ...current, [field]: nextFieldError };
    });
  }

  async function submit() {
    try {
      setSubmitting(true);
      setResultMessage(null);
      await submitProject({
        ...state,
        platforms: parseCsv(platformInput),
        action_tags: parseCsv(actionTagsInput),
        image_urls: uploadedImageUrls,
        video_urls: uploadedVideoUrls,
      });
      setFieldErrors({});
      setResultMessage(t("success"));
      window.location.href = "/";
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        const backendErrors = mapBackendValidation(error.detail);
        if (Object.keys(backendErrors).length > 0) {
          setFieldErrors((prev) => ({ ...prev, ...backendErrors }));
          setStep(0);
          setResultMessage(t("fixHighlighted"));
          return;
        }
      }

      setResultMessage(t("submissionFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMediaUpload() {
    if (!mediaFiles.length) {
      return;
    }

    const imageFiles = mediaFiles.filter((file) => file.type.startsWith("image/"));
    const videoFiles = mediaFiles.filter((file) => file.type.startsWith("video/"));

    try {
      setUploadingMedia(true);
      const [imageUrls, videoUrls] = await Promise.all([
        imageFiles.length ? uploadMediaFiles(imageFiles, "image") : Promise.resolve([]),
        videoFiles.length ? uploadMediaFiles(videoFiles, "video") : Promise.resolve([]),
      ]);

      setUploadedImageUrls((prev) => [...prev, ...imageUrls]);
      setUploadedVideoUrls((prev) => [...prev, ...videoUrls]);
      setResultMessage(t("uploadSuccess", { images: imageUrls.length, videos: videoUrls.length }));
    } catch {
      setResultMessage(t("uploadFailed"));
    } finally {
      setUploadingMedia(false);
    }
  }

  const previewCardProject = {
    id: "preview",
    title: state.title || t("previewAppTitle"),
    tagline: state.tagline || t("previewTagline"),
    external_url: state.external_url || "https://example.com",
    favicon_url: null,
    favicon_fallback_url: null,
    action_tags: parseCsv(actionTagsInput),
  };

  return (
    <main className="mx-auto w-full max-w-3xl px-3 py-5 sm:px-5">
      <header className="rounded-3xl border border-zinc-300 bg-white/85 p-4 sm:p-5 dark:border-zinc-800 dark:bg-zinc-950/80">
        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-teal-600 dark:text-teal-300">
          <Sparkles className="h-3.5 w-3.5" />
          {t("studio")}
        </p>
        <h1 className="mt-2 text-3xl font-semibold text-zinc-900 dark:text-zinc-50">{t("title")}</h1>
        <p className="mt-2 text-zinc-600 dark:text-zinc-400">{t("subtitle")}</p>
      </header>

      <div className="mt-5 flex gap-2 overflow-auto">
        {STEPS.map((item, index) => {
          const StepIcon = STEP_ICONS[index];
          return (
          <span
            key={item}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
              index === step
                ? "border-teal-300 bg-teal-400 text-zinc-950"
                : "border-zinc-300 bg-zinc-100 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
            }`}
          >
            <StepIcon className="h-3.5 w-3.5" />
            {t(item)}
          </span>
          );
        })}
      </div>

      <section className="mt-4 rounded-2xl border border-zinc-300 bg-white/85 p-4 dark:border-zinc-800 dark:bg-zinc-900/70">
        {step === 0 ? (
          <div className="grid gap-3">
            <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-600 dark:text-zinc-500">
              <Type className="h-3.5 w-3.5" />
              {t("appTitle")}
            </label>
            <input
              value={state.title}
              onChange={(event) => updateField("title", event.target.value)}
              onBlur={() => validateFieldOnBlur("title")}
              placeholder={t("appTitlePlaceholder")}
              className={`rounded-md border bg-white px-3 py-2 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 ${fieldErrors.title ? "border-rose-400" : "border-zinc-300 dark:border-zinc-700"}`}
            />
            {fieldErrors.title ? <p className="text-xs text-rose-700 dark:text-rose-300">{fieldErrors.title}</p> : null}
            <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-600 dark:text-zinc-500">
              <Tag className="h-3.5 w-3.5" />
              {t("tagline")}
            </label>
            <input
              value={state.tagline}
              onChange={(event) => updateField("tagline", event.target.value)}
              onBlur={() => validateFieldOnBlur("tagline")}
              placeholder={t("taglinePlaceholder")}
              className={`rounded-md border bg-white px-3 py-2 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 ${fieldErrors.tagline ? "border-rose-400" : "border-zinc-300 dark:border-zinc-700"}`}
            />
            {fieldErrors.tagline ? <p className="text-xs text-rose-700 dark:text-rose-300">{fieldErrors.tagline}</p> : null}
            <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-600 dark:text-zinc-500">
              <Globe className="h-3.5 w-3.5" />
              {t("externalUrl")}
            </label>
            <input
              value={state.external_url}
              onChange={(event) => updateField("external_url", event.target.value)}
              onBlur={() => validateFieldOnBlur("external_url")}
              placeholder={t("externalUrlPlaceholder")}
              className={`rounded-md border bg-white px-3 py-2 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 ${fieldErrors.external_url ? "border-rose-400" : "border-zinc-300 dark:border-zinc-700"}`}
            />
            {fieldErrors.external_url ? <p className="text-xs text-rose-700 dark:text-rose-300">{fieldErrors.external_url}</p> : null}
            <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-600 dark:text-zinc-500">
              <Palette className="h-3.5 w-3.5" />
              {t("description")}
            </label>
            <textarea
              value={state.description}
              onChange={(event) => updateField("description", event.target.value)}
              onBlur={() => validateFieldOnBlur("description")}
              placeholder={t("descriptionPlaceholder")}
              className={`min-h-36 rounded-md border bg-white px-3 py-2 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 ${fieldErrors.description ? "border-rose-400" : "border-zinc-300 dark:border-zinc-700"}`}
            />
            {fieldErrors.description ? <p className="text-xs text-rose-700 dark:text-rose-300">{fieldErrors.description}</p> : null}
          </div>
        ) : null}

        {step === 1 ? (
          <div className="grid gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
              <Layers className="h-4 w-4" />
              {t("platforms")}
            </label>
            <input
              value={platformInput}
              onChange={(event) => setPlatformInput(event.target.value)}
              className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            />
            <label className="inline-flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
              <Tag className="h-4 w-4" />
              {t("actionTags")}
            </label>
            <input
              value={actionTagsInput}
              onChange={(event) => setActionTagsInput(event.target.value)}
              className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            />
          </div>
        ) : null}

        {step === 2 ? (
          <div className="grid gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
              <Clapperboard className="h-4 w-4" />
              {t("uploadLabel")}
            </label>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                setMediaFiles(files);
                const nextImagePreviewUrls = files
                  .filter((file) => file.type.startsWith("image/"))
                  .map((file) => URL.createObjectURL(file));
                const nextVideoPreviewUrls = files
                  .filter((file) => file.type.startsWith("video/"))
                  .map((file) => URL.createObjectURL(file));

                setLocalPreviewImageUrls((prev) => {
                  for (const url of prev) {
                    URL.revokeObjectURL(url);
                  }
                  return nextImagePreviewUrls;
                });

                setLocalPreviewVideoUrls((prev) => {
                  for (const url of prev) {
                    URL.revokeObjectURL(url);
                  }
                  return nextVideoPreviewUrls;
                });
              }}
              className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            />
            <button
              type="button"
              disabled={uploadingMedia || mediaFiles.length === 0}
              onClick={handleMediaUpload}
              className="inline-flex w-fit items-center gap-2 rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-700 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200"
            >
              <Upload className="h-4 w-4" />
              {uploadingMedia ? t("uploadingMedia") : t("uploadSelected")}
            </button>

            {uploadedImageUrls.length || uploadedVideoUrls.length ? (
              <div className="inline-flex items-center gap-2 rounded-md border border-zinc-300 bg-zinc-100 p-3 text-xs text-zinc-700 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-300">
                <BadgeCheck className="h-4 w-4 text-teal-600 dark:text-teal-300" />
                {t("mediaReady", { images: uploadedImageUrls.length, videos: uploadedVideoUrls.length })}
              </div>
            ) : null}

            <div className="mt-2 space-y-2">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">{t("livePreview")}</p>
              <ProjectMediaCarousel imageUrls={previewImageUrls} videoUrls={previewVideoUrls} title={state.title || t("previewTitle")} />
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-5 text-sm text-zinc-700 dark:text-zinc-300">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-700 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300">
                <BadgeCheck className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">{t("finalPreview")}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-500">{t("finalPreviewHint")}</p>
              </div>
            </div>

            <ProjectMediaCarousel imageUrls={uploadedImageUrls} videoUrls={uploadedVideoUrls} title={state.title || t("previewTitle")} />

            <ProjectCard project={previewCardProject} />

            <div className="rounded-xl border border-zinc-300 bg-zinc-100/80 p-4 dark:border-zinc-800 dark:bg-zinc-950/60">
              <p className="text-zinc-900 dark:text-zinc-100">{state.description || t("descriptionPreview")}</p>
              <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-500">{t("platformSummary", { value: platformInput || "-" })}</p>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep((prev) => Math.max(0, prev - 1))}
            className="rounded-md border border-zinc-300 px-4 py-2 text-sm text-zinc-700 disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-300"
          >
            {t("back")}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              disabled={!canContinue}
              onClick={() => {
                const errors = validateBasics(state, t);
                setFieldErrors(errors);
                if (step === 0 && Object.keys(errors).length > 0) {
                  setResultMessage(t("fixHighlighted"));
                  return;
                }
                setResultMessage(null);
                setStep((prev) => Math.min(STEPS.length - 1, prev + 1));
              }}
              className="rounded-md bg-teal-400 px-4 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-50"
            >
              {t("next")}
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={submit}
              className="rounded-md bg-teal-400 px-4 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-50"
            >
              {submitting ? t("submitting") : t("submitProject")}
            </button>
          )}
        </div>

        {resultMessage ? <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300">{resultMessage}</p> : null}
      </section>
    </main>
  );
}
